import { useState } from 'react'
import { trackEvent } from '@/utils/analytics'
import { toast } from 'sonner'
import { useTranslation } from '@/i18n'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { Button } from '@/components/ui/button'
import { Edit, Trash, CircleStop, CirclePlay, Download, Loader2, Copy, Telescope, RotateCcw } from 'lucide-react'
import ConnectorStatusTag from './ConnectorStatusTag'
import EventhouseProvisionTag from './EventhouseProvisionTag'
import ScopeModal from './ScopeModal'
import {
  ConnectorRecord,
  ConnectorEnum,
  ConnectorStatus,
  EventhouseProvisionStatus,
  displayProvisionStatus,
  getEventhouseProvisionState,
} from '@/types/connector'
import { getConnectorStatus } from '@/lib/getConnectorStatus'
import { useAppStore } from '../store'

// TODO: see if this should be removed and use a function to create the service client credentials
import { apiService } from '@/service/api'


type ConnectorDataTableProps = {
  data: ConnectorRecord[]
}

const EmptyDataRow = ({ t }: { t: ReturnType<typeof useTranslation>['t'] }) => (
  <TableRow>
    <TableCell colSpan={6} className="text-center">
      {t('common:table_messages.no_connectors_found')}
    </TableCell>
  </TableRow>
)

const ConnectorDataTable = ({ data }: ConnectorDataTableProps) => {
  const { t } = useTranslation()
  const {
    setShowConnectorDetailsModal,
    setConnectorEditData,
    activateConnector,
    deleteConnector,
    advanceEventhouseProvision,
  } = useAppStore()

  const [isDownloading, setIsDownloading] = useState(false)
  // AF Discovery is a per-row action (needs a live connector); null = modal closed.
  const [scopeConnector, setScopeConnector] = useState<ConnectorRecord | null>(null)

  const handleCopyId = async (connectorId: string) => {
    try {
      await navigator.clipboard.writeText(connectorId)
      toast.success(t('connectors:connector_id_copied', { defaultValue: 'Connector ID copied' }))
    } catch {
      // clipboard can reject (permission denied / non-secure context) — don't show a false success
      toast.error(t('connectors:connector_id_copy_failed', { defaultValue: 'Failed to copy connector ID' }))
    }
  }
  
  const CONNECTORS_COLS = [
    t('common:table_columns.name'),
    t('common:table_columns.type'),
    t('common:table_columns.description'),
    t('common:table_columns.status'),
    t('common:table_columns.actions')
  ]

  const handleDownloadJson = async (connector: ConnectorRecord) => {
    setIsDownloading(true)
    const request = {
      connector_name: connector.name,
      connector_id: connector.id,
    }
    // Note: This method creates a service client, user needs to have the
    // permission to create a service client in the tenant
    try {
      const serviceClientKey = await apiService.createConnectorServiceClient(
        request
      )

      const dataToDownload = {
        url: `${window.location.protocol}//${window.location.hostname}`,
        tenant_id: connector.tenant_id,
        connector_id: connector.id,
        credentials: {
          key: serviceClientKey,
        },
      }

      const jsonString = JSON.stringify(dataToDownload, null, 2)
      const blob = new Blob([jsonString], { type: 'application/json' })
      const link = document.createElement('a')
      link.href = URL.createObjectURL(blob)
      link.download = `connector_config.json`
      document.body.appendChild(link)
      link.click()
      trackEvent('connector-config_downloaded', { connector_id: connector.id, connector_type: connector.type })
      document.body.removeChild(link)
    } catch (error) {
      // TODO: See if there is a better way to handle this error
      if (error instanceof Error && error.message === 'Forbidden') {
        toast.error(t('common:errors.permission_denied'))
        return
      }
      toast.error(t('common:errors.service_client_error'))
      return
    } finally {
      setIsDownloading(false)
    }
  }

  return (
    <>
    <Table>
      <TableHeader className="bg-slate-50 dark:bg-dark-muted">
        <TableRow>
          {CONNECTORS_COLS.map((col) => (
            <TableHead
              key={col}
              className={`${col === t('common:table_columns.actions') ? 'w-[140px]' : ''}`}
            >
              {col}
            </TableHead>
          ))}
        </TableRow>
      </TableHeader>
      <TableBody>
        {data.length > 0 ? (
          data.map((connector) => (
            <TableRow key={connector.id}>
              <TableCell>
                <span className="group inline-flex items-center gap-1">
                  <span className="font-semibold">{connector.name}</span>
                  <button
                    type="button"
                    className="opacity-0 group-hover:opacity-50 hover:!opacity-100 transition-opacity shrink-0"
                    title={t('connectors:copy_connector_id', { defaultValue: 'Copy connector ID' })}
                    aria-label={t('connectors:copy_connector_id', { defaultValue: 'Copy connector ID' })}
                    onClick={() => handleCopyId(connector.id)}
                  >
                    <Copy className="w-3.5 h-3.5 text-muted-foreground" />
                  </button>
                </span>
              </TableCell>
              <TableCell>{connector.type.toUpperCase()}</TableCell>
              <TableCell>{connector.description}</TableCell>
              <TableCell>
                {connector.type === ConnectorEnum.EVENTHOUSE ? (
                  // Eventhouse rows never heartbeat — the online/disconnected
                  // derivation would decay 60s after linking. Render the
                  // provisioning state machine instead.
                  <EventhouseProvisionTag
                    status={displayProvisionStatus(
                      getEventhouseProvisionState(connector)
                    )}
                    error={getEventhouseProvisionState(connector).error}
                  />
                ) : (
                  <ConnectorStatusTag status={getConnectorStatus(connector)} />
                )}
              </TableCell>
              <TableCell>
                <div className="flex gap-2">
                  {/* Download/Start-Stop drive the polling-agent contract
                      (service client + heartbeat) — meaningless for a
                      platform-provisioned eventhouse. */}
                  {connector.type !== ConnectorEnum.EVENTHOUSE && (
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => handleDownloadJson(connector)}
                    >
                      {isDownloading ? (
                        <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                      ) : (
                        <Download className="w-4 h-4 mr-1" />
                      )}
                    </Button>
                  )}
                  <Button
                    size="sm"
                    onClick={() => {
                      setConnectorEditData(connector)
                      setShowConnectorDetailsModal(true)
                    }}
                  >
                    <Edit className="w-4 h-4 mr-1" />
                    {t('common:buttons.edit')}
                  </Button>
                  {connector.type === ConnectorEnum.EVENTHOUSE &&
                    displayProvisionStatus(
                      getEventhouseProvisionState(connector)
                    ) === EventhouseProvisionStatus.ERROR && (
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => advanceEventhouseProvision(connector.id)}
                      >
                        <RotateCcw className="w-4 h-4 mr-1" />
                        {t('connectors:provision.retry')}
                      </Button>
                    )}
                  {connector.type === ConnectorEnum.PI && (
                    <Button
                      size="sm"
                      variant="outline"
                      disabled={
                        getConnectorStatus(connector) !== ConnectorStatus.ONLINE
                      }
                      title={
                        getConnectorStatus(connector) !== ConnectorStatus.ONLINE
                          ? t('connectors:scope.requires_online')
                          : t('connectors:scope.action')
                      }
                      onClick={() => setScopeConnector(connector)}
                    >
                      <Telescope className="w-4 h-4 mr-1" />
                      {t('connectors:scope.action')}
                    </Button>
                  )}
                  {connector.type !== ConnectorEnum.EVENTHOUSE &&
                    (connector.active ? (
                      <Button
                        size="sm"
                        variant="destructive"
                        onClick={async () => {
                          const success = await activateConnector(connector.id, false)
                          if (success) {
                            trackEvent('connector-activated', { connector_id: connector.id, connector_type: connector.type, is_active: false })
                          }
                        }}
                      >
                        <CircleStop className="w-4 h-4" />
                        {t('common:buttons.stop')}
                      </Button>
                    ) : (
                      <Button
                        size="sm"
                        onClick={async () => {
                          const success = await activateConnector(connector.id, true)
                          if (success) {
                            trackEvent('connector-activated', { connector_id: connector.id, connector_type: connector.type, is_active: true })
                          }
                        }}
                      >
                        <CirclePlay className="w-4 h-4" />
                        {t('common:buttons.start', { defaultValue: 'Start' })}
                      </Button>
                    ))}
                  <Button
                    size="sm"
                    variant="destructive"
                    onClick={async () => {
                      const success = await deleteConnector(connector.id)
                      if (success) {
                        trackEvent('connector-deleted', { connector_id: connector.id, connector_type: connector.type })
                      }
                    }}
                  >
                    <Trash className="w-4 h-4" />
                  </Button>
                </div>
              </TableCell>
            </TableRow>
          ))
        ) : (
          <EmptyDataRow t={t} />
        )}
      </TableBody>
    </Table>
    <ScopeModal
      key={scopeConnector?.id}
      isOpen={scopeConnector !== null}
      connector={scopeConnector}
      onClose={() => setScopeConnector(null)}
    />
    </>
  )
}

export default ConnectorDataTable
